import { useCallback, useEffect, useState, useRef } from 'react';
import axios from 'axios';

import { errorText } from '../components/custom_creative_session/DevelopmentStage';
import { visibleForSession } from './sessionScope';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * The Angle Selection step's behaviour — ONE implementation, two presentations.
 *
 * WAVE 3, SCREEN 2. Same contract as `usePsychologyMap`: v1 and v2 render this
 * step differently and must never decide differently.
 *
 * WHAT THE SWEEP FOUND, measured live on 2026-09-09 before anything was ported:
 *
 *   1. A stray `)}` left by the Create Custom removal rendered as literal text
 *      under the buttons. Proved by rendering the component — which no test had
 *      ever done; every existing test read the source as a string.
 *   2. `fetchAngles` caught every error into `console.error` and left the list
 *      empty, so a failed request and an offer with no angles were the same
 *      screen. Rule 10.
 *   3. A NEW session never fetched at all, and nothing said so — a third way to
 *      arrive at that same empty screen. This is the exact shape of the
 *      regression the Founder caught on screen 1.
 *   4. Nothing was scoped to a session. 456 angles live, **0 carrying a
 *      session_id**, one offer holding 83 — all of them shown at once on a
 *      resumed session.
 *   5. Generation appended with no notice of repeats. 11 of 26 offers hold
 *      duplicate angle names; the worst has 54 angles and 37 unique.
 *   6. Four failure paths used `alert()`.
 */

// Both lists are pinned to the backend's `feedback_wave2._ENTRY_POINTS` and
// `_AWARENESS_STAGES`. Checked value by value on 2026-09-09: they match. A
// value offered here and rejected there would be a control that looks like it
// works.
export const ENTRY_POINTS = [
  'FEAR', 'ASPIRATION', 'FRUSTRATION', 'CURIOSITY', 'AUTHORITY', 'SOCIAL_PROOF',
];
export const AWARENESS_STAGES = [
  'UNAWARE', 'PROBLEM_AWARE', 'SOLUTION_AWARE', 'PRODUCT_AWARE', 'MOST_AWARE',
];

/**
 * Which angles belong on screen, and what is being left off.
 *
 * `scopeToSession` alone is not enough here, and using it alone would repeat
 * the mistake that put an empty Psychology Map in front of the Founder. Every
 * angle in production predates session stamping, so scoping a RESUMED session
 * strictly would hide the operator's own previous work and show nothing.
 *
 *   new session      strict. The point of a new session is to start clean, and
 *                    its angles carry its id from this build onward. What the
 *                    offer holds is counted, not shown.
 *   resumed, stamped strict. This session's rows are known.
 *   resumed, none    FALL BACK to the unstamped rows and say so. An empty
 *                    screen with the answer sitting in the response is a bug,
 *                    not a scoping rule.
 */
export const visibleAngles = (all, sessionId, opts) =>
  visibleForSession(all, sessionId, opts);

export function useAngles({ offer, sessionId, isNewSession, sessionStartTime,
                            onComplete, onMultiAngleSelect, onTracksCreated,
                            refreshKey = 0 }) {
  const [allAngles, setAllAngles] = useState([]);
  const [selectedAngles, setSelectedAngles] = useState([]);
  //: Which session the current selection belongs to, so a move to another one
  //: resets it instead of carrying it across.
  const seededFor = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [feedback, setFeedback] = useState('');

  // THREE OUTCOMES, NEVER ONE (rule 10). `idle` has never asked; `loading` is
  // asking; `ok` asked and got an answer, whatever its size; `failed` could not
  // ask. Before this, all four produced an empty grid and no words.
  const [loadState, setLoadState] = useState('idle');
  const [loadError, setLoadError] = useState(null);

  const [actionError, setActionError] = useState(null);

  // `{ text, count }` — an identical repeat says so rather than re-rendering the
  // same string and looking like a dead button. Screen 1's lesson, carried.
  const [note, setNoteState] = useState(null);
  const setNote = (text) => {
    setNoteState((prev) =>
      !text ? null
      : prev && prev.text === text ? { text, count: prev.count + 1 }
      : { text, count: 1 });
  };

  const fetchAngles = useCallback(async () => {
    setLoadState('loading');
    try {
      const res = await axios.get(`${API}/offers/${offer.offer_id}/angles`);
      const rows = res.data.angles || [];
      setAllAngles(rows);
      /* SEED THE SELECTION FROM WHAT THIS SESSION CAN SEE, NOT FROM THE WHOLE
         OFFER. 2026-09-14, Founder's report.

         This used to be `rows.filter((a) => a.selected)` over EVERY angle on
         the offer, while the grid renders only the angles scoped to this
         session. So an angle selected in a PREVIOUS session was seeded into
         this one, filtered out of the grid, and still counted — the Founder saw
         one box ticked and "2 angles selected", then none ticked and "1 angle
         selected".

         Proven against production: offer `off_8ca85fff1389` holds 25 angles,
         one marked selected — `ang_1a95446508d0`, "The Closing Window Angle",
         from session `cs_3e84bf7b959e` — while he was in `cs_b4477b60d209`.
         **27 offers carry such an angle**, so every new session on any of them
         started with an invisible selection.

         And the count was the smaller half of it: `proceed()` passes
         `selectedAngles`, and under MA-3 each selected angle opens its own
         creative session — so this would open a full session for an angle
         nobody chose and nobody could see.

         `visibleAngles` is the same function the grid uses, so the selection
         and the boxes on screen cannot disagree again. The offer-level
         `selected_angle_id` pointer is untouched: the Founder ruled on
         2026-09-08 that it stays informational, and it is not the bug. */
      const opts = { isNewSession, sessionStartTime };
      const visible = visibleAngles(rows, sessionId, opts).rows;
      const visibleIds = new Set(visible.map((a) => a.angle_id));
      const already = rows
        .filter((a) => a.selected && visibleIds.has(a.angle_id))
        .map((a) => a.angle_id);

      /* A SESSION CHANGE IS AUTHORITATIVE; A REFETCH WITHIN ONE IS NOT.
         Caught by a test written for the dependency list above: seeding alone
         never CLEARED, so moving to a session with no selection left the
         previous session's angle sitting in state — the same phantom, one step
         later.
         The `if (already.length)` guard is kept for refetches inside a session,
         where it protects ticks the operator has made but not yet confirmed
         (`generateAngles` appends without refetching, so the only in-session
         refetch is an explicit `refreshKey` bump). When the SESSION changes,
         the new session's angles are the truth, empty included. */
      const sameSession = seededFor.current === sessionId;
      if (!sameSession) {
        setSelectedAngles(already);
        seededFor.current = sessionId;
      } else if (already.length) {
        setSelectedAngles(already);
      }
      setLoadState('ok');
      setLoadError(null);
    } catch (error) {
      // NOT swallowed. An offer with no angles and an unreachable backend used
      // to be the same blank grid.
      setLoadState('failed');
      setLoadError(errorText(error));
    }
    // `sessionId`, `isNewSession` and `sessionStartTime` are read inside, for
    // the visibility scope above. Without them here the callback would keep the
    // scope of whichever session it was first built for, and seed the selection
    // from that one — the same class of bug as the one it is fixing.
  }, [offer.offer_id, sessionId, isNewSession, sessionStartTime]);

  useEffect(() => {
    fetchAngles();
  }, [fetchAngles, refreshKey]);

  const generateAngles = async () => {
    setGenerating(true);
    setActionError(null);
    try {
      const res = await axios.post(`${API}/offers/${offer.offer_id}/angles/generate`, {
        feedback: feedback || undefined,
        session_id: sessionId || undefined,
      });
      const returned = res.data.angles || [];
      // The backend salvages what parses from a malformed answer and says what
      // it lost. Surfaced verbatim — a partial batch presented as a whole one
      // is the silent success this screen family keeps being fixed for.
      const partial = res.data.partial_note || null;

      // Computed from the CURRENT list, synchronously. Reading it back inside
      // the state updater gave 0 every time on screen 1 — the updater had not
      // run yet — and announced the opposite of what happened.
      const onScreen = new Set(
        allAngles.map((a) => (a.angle_name || '').trim().toLowerCase()));
      const repeats = returned.filter(
        (a) => onScreen.has((a.angle_name || '').trim().toLowerCase())).length;

      if (returned.length) setAllAngles((prev) => [...prev, ...returned]);
      setFeedback('');

      // Same rule as `useHooks`: count what APPEARED, not what came back. The
      // Hooks screen shipped saying "Added 5" while showing none, because the
      // two lists had diverged. Angles are stamped (MA-1) so this should always
      // agree — "should" is why it is checked rather than assumed.
      const opts = { isNewSession, sessionStartTime };
      const before = visibleAngles(allAngles, sessionId, opts).rows.length;
      const after = visibleAngles([...allAngles, ...returned], sessionId, opts).rows.length;
      const appeared = after - before;

      // Angles are separate records with their own ids, so a repeated NAME is
      // not automatically a duplicate row and is not dropped. It is said out
      // loud instead: 11 of 26 offers already hold repeated names, and an
      // operator who cannot see that keeps regenerating into the pile.
      const withPartial = (t) => (partial ? `${partial} ${t}` : t);
      setNote(withPartial(
        returned.length === 0
          ? 'The model returned no angles. Nothing was added.'
          : appeared === 0
            ? `Saved ${returned.length} angle${returned.length > 1 ? 's' : ''}, but `
              + `none of them are shown here — this view is scoped to the current `
              + `session. They are on the offer.`
          : repeats === 0
            ? `Added ${returned.length} angle${returned.length > 1 ? 's' : ''}.`
            : repeats === returned.length
              ? `Added ${returned.length}, but every one repeats the name of an angle already on screen.`
              : `Added ${returned.length}; ${repeats} repeat${repeats > 1 ? '' : 's'} the name of an angle already on screen.`));
    } catch (error) {
      setActionError(errorText(error));
    } finally {
      setGenerating(false);
    }
  };

  const toggleAngle = (angleId) => {
    setSelectedAngles((prev) =>
      prev.includes(angleId) ? prev.filter((id) => id !== angleId) : [...prev, angleId]);
  };

  /**
   * Commit the chosen angles — as REAL SESSIONS, one per angle (MA-3).
   *
   * Founder ruling: several angles means one full, independent creative session
   * each, running to completion and submitting whenever ready. So this calls
   * MA-1's `tracks` endpoint: the session the operator is in becomes track 1
   * and a sibling opens for every other angle.
   *
   * WHAT THIS REPLACES. Before MA-3 the extra angles lived in React state and
   * drove an in-memory loop that covered two of the five steps and was lost on
   * reload — and in v2 there was no loop at all behind the "Angle N of M"
   * banner. The banner claimed a run the shell never performed.
   *
   * `angles/{id}/select` is still called for the first angle. It writes
   * `offers.selected_angle_id`, which the Founder ruled stays as informational
   * state (2026-09-09) — several readers outside the creative engine still use
   * it, and this lane does not disturb them.
   *
   * A FAILURE TO CUT THE TRACKS DOES NOT ADVANCE THE STEP. Moving on would
   * leave the operator in a session bound to nothing, believing three tracks
   * existed.
   */
  const proceed = async (ids) => {
    const chosen = ids || selectedAngles;
    if (chosen.length === 0) {
      setActionError('Select at least one angle to continue.');
      return;
    }
    setSelecting(true);
    setActionError(null);
    try {
      await axios.post(`${API}/offers/${offer.offer_id}/angles/${chosen[0]}/select`);

      if (sessionId) {
        const res = await axios.post(`${API}/creative/sessions/${sessionId}/tracks`,
                                     { angle_ids: chosen });
        if (onTracksCreated) onTracksCreated(res.data);
      }

      const objects = chosen.map((id) => allAngles.find((a) => a.angle_id === id)).filter(Boolean);
      if (onMultiAngleSelect) onMultiAngleSelect(objects);
      if (onComplete) onComplete();
    } catch (error) {
      setActionError(errorText(error));
    } finally {
      setSelecting(false);
    }
  };

  const refineAngle = async (angleId, refineFeedback) => {
    const text = (refineFeedback || '').trim();
    if (text.length < 3) return false;
    setActionError(null);
    try {
      const res = await axios.post(
        `${API}/offers/${offer.offer_id}/angles/${angleId}/refine`, { feedback: text });
      const updated = res.data.angle;
      if (updated?.angle_id) {
        setAllAngles((prev) => prev.map(
          (a) => (a.angle_id === updated.angle_id ? { ...a, ...updated } : a)));
      }
      return true;
    } catch (error) {
      setActionError(errorText(error));
      return false;
    }
  };

  const saveAngleEdits = async (angleId, draft) => {
    try {
      const res = await axios.patch(
        `${API}/offers/${offer.offer_id}/angles/${angleId}`, draft);
      const updated = res.data?.angle;
      if (updated?.angle_id) {
        setAllAngles((prev) => prev.map(
          (a) => (a.angle_id === updated.angle_id ? { ...a, ...updated } : a)));
      }
      return { ok: true };
    } catch (error) {
      // The backend distinguishes an unknown field, an emptied required field
      // and a bad enumeration value. Returned rather than alerted so the caller
      // can put it next to the field it is about.
      return { ok: false, error: errorText(error) };
    }
  };

  const view = visibleAngles(allAngles, sessionId, { isNewSession, sessionStartTime });

  return {
    angles: view.rows,
    hiddenOtherSession: view.hiddenOtherSession,
    hiddenUnstamped: view.hiddenUnstamped,
    predateNote: view.predateNote,
    allCount: allAngles.length,
    selectedAngles, toggleAngle,
    loadState, loadError, retryLoad: fetchAngles,
    generating, generateAngles, feedback, setFeedback,
    selecting, proceed,
    refineAngle, saveAngleEdits,
    actionError, setActionError,
    note,
    // True when the operator has picked more than one angle. MA-3 turned this
    // from a WARNING into a description: each angle now gets its own session,
    // so nothing is lost. Both screens say what will happen before Continue.
    multiAngleWarning: selectedAngles.length > 1,
  };
}
